import { Component } from '@theme/component';

/**
 * @typedef {Object} BackToTopRefs
 * @property {HTMLButtonElement} button - The back-to-top button
 */

/**
 * A custom element that shows a fixed back-to-top button, horizontally centered under
 * the collection/search facets sidebar, once the viewport has scrolled past the bottom of
 * the first facet (e.g. Price) in that sidebar. Hides again before it would overlap the
 * footer, and scrolls the page back to the top on click.
 *
 * @extends {Component<BackToTopRefs>}
 */
class BackToTopComponent extends Component {
  requiredRefs = ['button'];

  /** @type {IntersectionObserver | null} */
  #sidebarObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {ResizeObserver | null} */
  #sidebarResizeObserver = null;

  /** @type {MutationObserver | null} */
  #facetsMutationObserver = null;

  /** @type {HTMLElement | null} */
  #sentinel = null;

  /** @type {(() => void) | null} */
  #updateCenter = null;

  /** @type {boolean} */
  #pastSidebar = false;

  /** @type {boolean} */
  #footerVisible = false;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();

    const sidebar = document.querySelector('.collection-sidebar');
    if (sidebar) {
      this.#setupSidebarObserver(sidebar);
      this.#setupCentering();
    }

    this.#setupFooterObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#sidebarObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#sidebarResizeObserver?.disconnect();
    this.#facetsMutationObserver?.disconnect();
    this.#abortController?.abort();
    this.#sentinel?.remove();
  }

  /**
   * `data-visible` and the `--back-to-top-center-x` inline style are both runtime-only
   * state, so morph.js strips them on every filter/sort/pagination re-render (neither is
   * in PRESERVED_ATTRIBUTES, and `style` is only carried forward for a couple of
   * special-cased tags that aren't this one). Reapply both from cached state / a fresh
   * measurement right after morph updates refs.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#updateVisibility();
    this.#updateCenter?.();
  }

  /**
   * Tracks whether the viewport has scrolled past the *bottom* of the first facet (e.g.
   * Price) in the sidebar — not the whole (often much taller) sidebar, and not just its
   * top edge. A zero-height sentinel placed right after that first `.facets__item` gives
   * IntersectionObserver a single point to watch there.
   *
   * Facets can be populated asynchronously (Dynamic Yield fetches them client-side), so a
   * MutationObserver repositions the sentinel once the first facet actually exists, falling
   * back to the top of the sidebar until then.
   *
   * @param {Element} sidebar
   */
  #setupSidebarObserver(sidebar) {
    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'height: 0; pointer-events: none;';
    this.#sentinel = sentinel;

    const placeSentinel = () => {
      const firstFacet = sidebar.querySelector('.facets__item');
      if (firstFacet) {
        // Skip the move if it's already correctly placed — repositioning via `.after()`
        // fires a childList mutation even when the position doesn't actually change,
        // which would make this MutationObserver retrigger itself forever.
        if (firstFacet.nextSibling !== sentinel) firstFacet.after(sentinel);
      } else if (!sentinel.isConnected) {
        sidebar.prepend(sentinel);
      }
    };
    placeSentinel();

    this.#facetsMutationObserver = new MutationObserver(placeSentinel);
    this.#facetsMutationObserver.observe(sidebar, { childList: true, subtree: true });

    this.#sidebarObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0) this.#pastSidebar = true;
      } else {
        this.#pastSidebar = false;
      }

      this.#updateVisibility();
    });

    this.#sidebarObserver.observe(sentinel);
  }

  /**
   * Keeps the button horizontally centered under the facets sidebar by tracking its
   * horizontal position — a fixed-position element can't rely on CSS alone for this
   * since it isn't a descendant of the sidebar.
   *
   * Measures the sentinel rather than `.collection-sidebar` itself: in the standard
   * Horizon layout (no category nav, no vertical filter panel), base.css collapses
   * `.collection-sidebar` to `display: contents`, so it generates no box and
   * `getBoundingClientRect()` would return a zero-width rect. The sentinel is a real
   * child element, so it always has a genuine box in either layout.
   */
  #setupCentering() {
    const sentinel = this.#sentinel;
    if (!sentinel) return;

    const { signal } = /** @type {AbortController} */ (this.#abortController);

    this.#updateCenter = () => {
      const rect = sentinel.getBoundingClientRect();
      this.style.setProperty('--back-to-top-center-x', `${rect.left + rect.width / 2}px`);
    };

    this.#sidebarResizeObserver = new ResizeObserver(this.#updateCenter);
    this.#sidebarResizeObserver.observe(sentinel);
    window.addEventListener('resize', this.#updateCenter, { signal });
    this.#updateCenter();
  }

  /**
   * Watches the footer and hides the button before it would overlap it, mirroring the
   * approach used by sticky-add-to-cart.js.
   */
  #setupFooterObserver() {
    // In themes migrated from 2.0, the footer element doesn't exist.
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        this.#footerVisible = entry.isIntersecting;
        this.#updateVisibility();
      },
      {
        // Hides the button 200px before the footer enters the viewport.
        rootMargin: '0px 0px 200px 0px',
      }
    );
    this.#footerObserver.observe(footer);
  }

  /** Applies the combined sidebar/footer state to the button's visibility. */
  #updateVisibility() {
    this.dataset.visible = String(this.#pastSidebar && !this.#footerVisible);
  }

  /**
   * Scrolls the page back to the top, respecting reduced-motion preferences.
   */
  scrollToTop = () => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#getScrollContainer().scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  };

  /**
   * Finds the element that's actually scrolling the page. At desktop widths `.page-wrapper`
   * (not the document) is the scroll container — see `html:has(.page-wrapper)` in base.css.
   *
   * @returns {Element}
   */
  #getScrollContainer() {
    const pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper && pageWrapper.scrollHeight > pageWrapper.clientHeight) return pageWrapper;
    return document.scrollingElement ?? document.documentElement;
  }
}

if (!customElements.get('back-to-top-component')) {
  customElements.define('back-to-top-component', BackToTopComponent);
}
